<script setup>
import { ref, watch, toRaw, computed, h, onMounted, onBeforeMount, onBeforeUpdate, onUnmounted } from "vue";
import YautyRow from '@/components/common/yautyTable/YautyRow.vue';
import YautyFilter from '@/components/common/yautyTable/YautyFilter.vue';
import YautyPaginator from '@/components/common/yautyTable/YautyPaginator.vue';
import YautyColumnToggler from '@/components/common/yautyTable/YautyColumnToggler.vue';
import { useI18n } from 'vue-i18n';
import LocalStorage from '@/controller/LocalStorage.js';
import FloatingFilters from "../floatingButton/FloatingFilters.vue";

const props = defineProps({
  isLoading: Boolean,
  offset: Number,
  limit: Number,
  sort: String,
  dir: String,
  filters: Object,
  items: Object,
  headers: Array,
  total: Number,
  page: Number,
  search: Object, // enabled, position, width
  permissions: Object,
  render: String, //server, client
  pagination: Boolean,
  expandable: Boolean,
  filtrable: Boolean,
  columnSelector: Boolean,
  table: String,
  tableStates: Object,
  theme: String,
  draggable: Boolean, //work in progress. Don't use it yet!!
  styles: Object});
const { t } = useI18n();
const brandTitle= t('vehicle.brand');
const statusTitle = t('workfiles.status');
const activeFilters = t('active-filters');


const minHeightNum = ref(props.limit * 8.4 + 'dvh');

const isMobileView = ref(()=> window.innerWidth <= 576);
const isDesktopView = ref(()=> window.innerWidth > 576)
const updateView = () => {
  isMobileView.value = window.innerWidth <= 576;
  isDesktopView.value = window.innerWidth > 576;
};
onMounted(() => {
  window.addEventListener('resize', updateView);
});


const f1 = '/assets/svg/F1.svg';
const f2 = '/assets/svg/F2.svg';
const f3 = '/assets/svg/F3.svg';
const filtersValue = ref(props.filters);
const emit = defineEmits(["callLoadPage","searching","callUpdateVehicle","filterChanged","sortChanged","updateLinkedClient","syncVehicle, searchChanged"]);
const pagNumber = ref(1);
const pagLimit = ref(props.limit ?? 12);
const reRender = ref(1);
const columnsVisibility = ref([]);
const columnHeaders = ref([]);
const previousSort = ref(null);
const userColor = ref(LocalStorage.getColor());
const loadBorders = ref('visible');
let clientItems,clientStart,clientEnd = undefined;

if(props.render == 'client'){
    clientItems = toRaw(props.items).slice(0, props.limit);
    clientItems = toRaw(clientItems);
}

function loadTablePage(idx) {
  let index = idx ?? 1;
  pagNumber.value = index;
  if(props.render == 'client'){
      clientStart = (pagLimit.value * (index - 1));
      clientEnd = pagLimit.value * index;
      clientItems = props.items.slice(clientStart, clientEnd);
  }else if(props.render == 'server'){
    emit('callLoadPage', index - 1);
  }
}
function filterChanged(newVal, name) {
  emit("filterChanged", newVal, name);
}
function toggleExpand(isExpanded, element, index) {
  emit("toggleExpand", isExpanded, element, index);
}
function updateLinkedClient(client){
  emit('updateLinkedClient', client);
}
function syncVehicle(clientId,action){
  emit('syncVehicle',clientId,action);
}
function updateSearch(searchValue){
  emit('searchChanged', searchValue);
}
function sortChanged(currentSort){
  if(currentSort.isSortable){
    if(currentSort.isSortActive){
      currentSort.dir = !currentSort.dir; //al primer click s'activa però no canvia la direcció
    }else{
      currentSort.isSortActive = true;
      currentSort.dir = false;
    }
  
    if(previousSort.value != null){
      if(previousSort.value.field != currentSort.field){
        previousSort.value.isSortActive = false;
        previousSort.value.dir = null;
      }
    }
    previousSort.value = currentSort;
    emit('sortChanged', currentSort.field, currentSort.dir);
  }
}

function reloadColumns(){
  columnHeaders.value =  props.headers.map(header => {
    let item = header;
    if(columnsVisibility.value){
      let column = columnsVisibility.value.find(column => column.name == header.name);
      if(column){
        item.visible = column.visible;
      }
    }
    return item;
  });
};
function columnToggled(){
  let columnsVis = localStorage.getItem('columns_'+props.table);
  if(columnsVis){
    columnsVisibility.value = JSON.parse(columnsVis);
  }
  reloadColumns();
}

watch(() => props.total, () => { reRender.value++; });
watch(() => columnsVisibility, () => { reloadColumns(); })

onBeforeMount(() => {
  columnToggled();
});
onBeforeUpdate(() => {
  loadBorders.value = 'visible';
})
onUnmounted(() => {
  loadBorders.value = 'hidden';
  window.removeEventListener('resize', updateView);
})
if(props.styles){
  for (const [key, value] of Object.entries(props.styles)) {
    if(key == 'tdFirstChild'){
      document.documentElement.style.setProperty('--tdFirstChild', value);
    }
  }
}

const isActive = ref(false);

onMounted(() => {
  setTimeout(() => {
    isActive.value = true;
  }, 50);
});
</script>

<template>
  <div :class="{'custom-table-wrapper-home': props.table === 'homePendingWorkfiles', 'custom-table-wrapper': props.table !== 'homePendingWorkfiles' }">
    <div class="table-wrapper yautyTable" :class="{'minimal': props.theme == 'minimal', 'minim-height':isLoading}">
      <table class="custom-table table" role="table">
        <thead role="rowgroup">
          <tr role="row" class="tr-th">
            <th scope="col" v-for="h in columnHeaders" role="columnheader" class="columnheader" :style="{ 'min-width':  h.width}" :class="{'isSortable':h.isSortable, 'filtrable': props.filtrable, 'unfiltrable': !props.filtrable, 'fixedWidth': h.fixedWidth, 'modelFix' : h.field === 'model' && h.navigation === '/workfile/', 'client' : h.field === 'client', 'status-mobile' : h.field === 'status-mobile'}" v-show="h.visible">
              <div class="columnheader_inner">
                <div class="upperHeader" @click="sortChanged(h)">
                  <div class="column-header-title plate_vin--fr" v-if="h.field == 'plate_vin' && h.text=='Immatriculation/VIN'">
                    <span class="line">Immatriculation/</span>
                    <span class="line">VIN</span>
                  </div>
                  <div class="column-header-title" v-else 
                    :class="
                    {
                      'justify-content-start':h.justify == 'start',
                      'justify-content-center':h.justify == 'center',
                      'justify-content-end':h.justify == 'end',
                    }"
                    >{{ h.text }}
                  </div>
                  <div class="column-header-sort-icon" v-if="h.isSortable" :class="{'sortActive':h.isSortActive, 'sortNotActive': !h.isSortActive}">
                    <img :src="f1" alt="" v-if="h.dir == null">
                    <img :src="f2" alt="" v-if="h.dir == true">
                    <img :src="f3" alt="" v-if="h.dir == false">
                  </div>
                </div>
                <YautyFilter v-if="h.filtrable && h.name"
                  :type="h.filter.type"
                  :data="h.filter.data"
                  :src="h.filter.src"
                  :name="h.name"
                  :filters="filtersValue"
                  :position="h.filter.position"
                  :items="props.items"
                  @filterChanged="filterChanged"
                  @searchChanged="updateSearch"
                />
                <div v-else></div>
              </div>
            </th>
              <div class="column-toggler" v-if="props.columnSelector">
                <YautyColumnToggler 
                  :items="props.items"
                  :columns="columnHeaders" 
                  :table="props.table"
                  @columnToggled="columnToggled"
                  />
            </div>
          </tr>
        </thead>
        <tbody>
          <template v-if="isLoading">
            <YautyRow 
            :isLoading="isLoading"
            :columnsTotal="columnHeaders.length"
            />
          </template>
          <template v-else>
              <YautyRow
                v-for="(item, index) in items"
                :isLoading="isLoading"
                :data="item"
                :headers="columnHeaders"
                :columnsTotal="columnHeaders.length"
                :key="index"
                :index="index"
                :expandable="props.expandable"
                :isExpanded="item.isExpanded"
                :isExpansion="item.isExpansion"
                :tableStates="props.tableStates"
                :draggable="props.draggable"
                :table="props.table"
                @toggleExpand="toggleExpand"
                @updateLinkedClient="updateLinkedClient"
                @syncVehicle="syncVehicle"
              />
          </template>
          <template v-if="props.total == 0 && !isLoading">
            <YautyRow
              :noData="true"
              :isLoading="isLoading"
              :columnsTotal="columnHeaders.length"
            />
          </template>
          </tbody>
      </table>
    </div>
    <div class="custom-table-pagination" :class="{'minimal': props.theme == 'minimal'}" v-if="props.pagination && items.length">
      <YautyPaginator
          :total="props.total"
          :pagNumber="props.page"
          :limit="pagLimit"
          @loadPage="loadTablePage"
          @updateLimit="(newLimit) => pagLimit = newLimit"
          :key="reRender"
      />
    </div>
  </div>
  
  <FloatingFilters v-if="props.table === 'workfilesList' || props.table === 'vehicleBodyList'"
        :activeFilters="activeFilters"
        :brand="brandTitle" 
        :status="statusTitle"
        :headers="headers"
        :name="headers.name"
        :src="headers.filter.src"
        :filters="filtersValue"
        @filterChanged="filterChanged"
        />
</template>

<style lang="scss">
  ::-webkit-scrollbar {
      width: 10px;
      height: 6px;
  }
  ::-webkit-scrollbar-thumb {
    background: #8D939E; 
    }
    ::-webkit-scrollbar-thumb:hover {
    background: #555 !important; 
    }
    ::-webkit-scrollbar-track {
    background: #E5E8EC;
    }


.justify-content-center {
  display: flex;
  width: 100%;
  gap: 0.5rem;
  justify-content: center !important;
  padding-right: 2rem;
}

.justify-content-end {
  display: flex;
  width: 100%;
  gap: 0.5rem;
  justify-content: end !important;
  padding-right: 2rem;
}
// .yautyTable{
//   overflow-x: hidden !important;
//   overflow-y: visible !important;
// }

.custom-table-wrapper, .custom-table-wrapper-home{
  .table-wrapper{
      border-radius: 25px;
      @media (max-width:576px) {
        background-color: transparent;
        border-radius: 11px !important;
      }
      .column-toggler{
        border-bottom: none;
        position: absolute;
        font-family: 'CircularStd-Book';
        font-size: 16px;
        color: black;
        right: 1rem;
        z-index: 101;
        @media (max-width: 858px){
          display: none;
        }
      }
      .custom-table {
        table-layout: auto;
        width: 100%;
        font-size: 0.9em;
        background-color: white;
        color: #6e6c6c;
        width: 100%;
        display: table;
        border-radius: 25px;
        font-family: 'CircularStd-Book';
        padding: 1rem;
        padding-right: 2rem;
        // height: v-bind(minHeightNum);
        .td-loading {
          height: 100%;
          background-color: whitesmoke;
          .loading-cell {
            height: 400px;
            display: flex;
            align-items: center;
            justify-content: center;
          }
        }
        th,
        tr {
          padding: 4px 10px;
        }
        tr td {
          padding: 4px 15px;
          border-bottom: 2px solid white;
          border: solid 1px #d8d8d8;
          border-left: 0;
          border-right: 0;
          min-height: 60px;
          max-height: 100px;
          height: auto;
        }
        td{
          word-wrap: break-word;
          overflow-wrap: break-word;
          // white-space: nowrap;
        }
        
        th:nth-of-type(1){
          border-top-left-radius: 25px;
          padding-left: 2.5rem;
        }
        td:nth-of-type(1){
          padding-left: 2.5rem;
        }
        td.action_buttons:nth-of-type(1){
          // padding-left: 2.5rem;
        }
        th:nth-last-of-type(1) {
          border-top-right-radius: 25px;
          padding-right: 2.5rem;
          @media (max-width:576px) {
            padding-right: 0rem;
          }
        }
        tr:last-child td{
          border-bottom:none;
        }  
        thead th {
          color: #504e4e;
          background-color: white !important;
          height: 70px;
          &>div {
            align-items: center;
            display: flex;
            height: 100%;
            font-size: 16px;      
          }
          &.isSortable{
            cursor: pointer;
          }
        }
        tbody {
          tr {
            transition: background-color 150ms ease-out;
            // height: 60px;
            td:first-child:not(.cell-plate){
              // padding-left: var(--tdFirstChild) !important;
              padding-left: max(var(--tdFirstChild, 0.5rem), 2.5rem);
              padding-right: 0;
            }
          }

        }
        tbody td {
          background-clip: padding-box
        }
        th.columnheader{
          font-family:'CircularStd-Black';
        }
        th.columnheader:not(.fixedWidth){
          width: auto !important;
        }
        .columnheader_inner{
          display: flex;
          flex-direction: column;
          .upperHeader{
            display: flex;
            justify-content: flex-start;
            align-items: center;
            width: 100%;
            padding-top: 1rem;
            gap: 0.5rem;
            .column-header-sort-icon{
              font-size: 20px;
            }
            .sortActive{
              color: #33416B;
            }
            .sortNotActive{
              color: lightgrey;
            }
            .column-header-title{
              color: #33416B;
              font-family:'CircularStd-Book';
              height:38px;
              display: flex;
              align-items: center;
              &.plate_vin--fr{
                display: flex;
                flex-direction: column;
              }
            }
          }
          .custom-filter{
            margin-top: 5px;
            margin-bottom: 1rem;
          }
        }
        .filtrable{
          height: 100px;
        }
        .unfiltrable{
          .upperHeader{
            height: 100%;
            padding-top: 0;
          }
        }
      }
  }
  .table-wrapper.yautyTable:not(.minimal) {
        table{
          border-collapse: collapse;
          
          
        }
        &.minim-height table {
          height: v-bind(minHeightNum);
          
        }

        
        th,td{
          position: relative;
          border: none;
        }    
        td.td-expansion::before {
          left: 0px !important;
          width: 100% !important;
        }
        tr.expanded td.pr0.action_buttons::before {
          left: 0px;
          width: 100%;
        }
        tr.expanded td::before {
          width: 100% !important;
          right: 0px !important;
        }
        tr:not(:last-child) td:not(:first-child):not(:last-child)::before,
        th:not(:first-child):not(:last-child)::before {
          content: "";
          display: block;
          height: 1px;
          min-height: 1px;
          max-height: 1px;
          transform: scaleY(1);
          transform: translateZ(0);
          backface-visibility: hidden;
          will-change: transform;
          width: 100%;
          background-color: #ddd;
          overflow: hidden;
          position: absolute;
          bottom: 0px;
          left: 0px;
          z-index: 10;
          visibility: v-bind(loadBorders);
        }
        tr:not(:last-child) td:first-child::before,
        tr:not(:last-child) td:last-child::before,
        th:nth-of-type(1)::before,
        th:nth-last-of-type(1)::before{
          content: "";
          display: block;
          height: 1px;
          min-height: 1px;
          max-height: 1px;
          transform: scaleY(1);
          transform: translateZ(0);
          backface-visibility: hidden;
          will-change: transform;
          width: calc(100% + 0px) !important;
          background-color: #ddd;
          overflow: visible;
          position: absolute;
          bottom: 0px;
          left: 0 !important;
          z-index: 10;
          visibility: v-bind(loadBorders);
        }
        tr:not(.tr-no-data):hover td:first-child::after{
          content: "";
          display: block;
          height: 100%;
          width: 3px;
          background-color: v-bind(userColor);
          overflow: hidden;
          position: absolute;
          bottom: 0px;
          left: 0px;
          visibility: v-bind(loadBorders);
        }
        tr:not(.tr-no-data):last-child:hover td:first-child::after {
          content: "";
          display: block;
          height: 70%;
          width: 3px;
          background-color: v-bind(userColor);
          overflow: hidden;
          position: absolute;
          bottom: 30%;
          left: 0px;
          visibility: v-bind(loadBorders);
        }
        td:first-child::before,
        th:nth-of-type(1)::before {
          left: 20px;
          visibility: v-bind(loadBorders);
        }
        td:last-child::before,
        th:nth-last-of-type(1)::before {
          right: 20px;
          visibility: v-bind(loadBorders);
        }   
        td:first-child::after,
        th:nth-of-type(1)::after {
          left: 20px;
          visibility: v-bind(loadBorders);
        }
        td:last-child::after,
        th:nth-last-of-type(1)::after {
          right: 20px;
          visibility: v-bind(loadBorders);
        }   
  }
  .table-wrapper.minimal{
      .custom-table{
          th,tr,td {
            border: none; 
          }
          border-collapse: separate;
          border-spacing: 0 25px;
          thead{
            .columnheader_inner{
                height: unset;
                .column-header-title{
                  font: normal normal normal 22px/19px 'CircularStd-Bold';
                  letter-spacing: 0px;
                  color: #153440;
                  margin-bottom: -18px;
                }
            }
            th:first-child{
              padding-left: 1rem;
            }
            th{
              height: unset;
            }
          }
          tbody{
              tr {
                background-color: #F2F2F2;
                td::after{
                  padding: 10px;
                }
                td:first-child{
                  border-top-left-radius: 30px;
                  border-bottom-left-radius: 30px;
                  padding-left:1rem;
                }
                td:last-child{
                  border-top-right-radius: 30px;
                  border-bottom-right-radius: 30px;
                  padding-right:1rem;
                }
                .custom-table-cell{
                  min-height: 45px !important;
                }
              }
          }        
      } 
  }
  .custom-table-pagination{
    .pagination-current{
        padding-right: 2rem;
    }
  }
  .custom-table-pagination.minimal{
      padding: 0 1rem;
      .custom-table-pagination-wrapper{
        margin-top: -30px;
        margin-bottom: 0;
      }
  }
}
.modelFix, .status-mobile{
  display: none;
}
@media screen and (max-width: 576px) {
  tr:hover td:first-child::after{
          content: "";
          background-color: transparent !important;
        }
    table {
      border: 0;
      background-color: transparent !important;
    }
    table caption {
      font-size: 1.3em;
    }
    table thead {
      display: none;
    }
    
    table tr {
      display: block;
      margin-bottom: 2em;
      border-radius: 25px;
      background-color: unset;
    }
    .custom-table tbody td{
      min-height: 50px !important;
    }
    table td {
      position: relative;
      display: block;
      text-align: center;
      background-color: #ddd;
      border: none !important;
      height: 50px !important;
      .custom-table-cell{
        justify-content: center;
        align-self: center;
        padding-left: 6rem;
        height: 100% !important;
        @media (max-width:576px) {
          padding-left: 0;
          justify-content: flex-start;
        align-self: flex-start;
        }
        .cell-status{
          justify-content: center;
        }
        .cell-plate-vin{
          justify-content: center;
          align-items: center;
        }
        .cell-status-category{
          justify-content: start;
          align-items: center;
        }
        .cell-status-category span{
          padding: 0 !important;
          padding-left: 10px !important;
        }
        .cell-client{
          flex-direction: row !important;
          gap: 5px;
        }
      }
    }
    
    table td::before {
      @media (max-width: 576px) {
            display: none !important;
          }
      content: attr(data-label);
      float: left;
      position: absolute;
      top: 50%;
      left: 0;
      transform: translateY(-50%);
    }
    
    table td:last-child {
      border-bottom: 0;
    }
    table td:not(:last-child):not(.action_buttons) {
      border-top: 1px solid #ddd; 
    }
    td.pr2.action_buttons.table-cell {
      display: none;
      position: absolute !important;
      top: 20px !important;
      right: 0;
    }
    td.pr0.action_buttons.table-cell {
    display: none;
}
    
    .cell.cell-collapse {
      display: none !important;
    }
    table tr{
      background-color: white;
      border-radius: 11px !important;
    }
    .table-wrapper{
      background-color: transparent;
      overflow: hidden !important;
    }
    
    .custom-table{
      td:nth-of-type(1){
        order: 3 !important;
      }
      td:nth-of-type(2){
        order: 4 !important;
      }
      td:nth-of-type(3){
        order: 5 !important;
      }
      td:nth-of-type(4){
        order: 2 !important;
      }
      td:nth-of-type(5){
        order: 1 !important;
      }
    }

    .modelFix, .status-mobile{
  display: block !important;
}
}
td > div > div > div > a > svg:hover > g {
  > path, > circle, > line, > g > path {
    stroke: v-bind(userColor);
    transition: 0.3s linear;
  }
}
td > div > button > span > a > svg:hover > g {
  > circle, > g > path {
    stroke: v-bind(userColor);
    transition: 0.3s linear;
  }
}
.expansion-files, .expansion-invoices {
  > a {
    height: 1.5dvw;
    > img:hover {
      filter: invert(49%) sepia(23%) saturate(274%) hue-rotate(181deg) brightness(93%) contrast(87%);
      transition: 0.3s linear;
    }
  }
}
@media (min-width: 577px) and (max-width: 1280px) and (orientation: portrait){
  tr:hover td:first-child::after{
          content: "";
          background-color: transparent !important;
        }
  th.columnheader.filtrable.client {
    min-width: 170px !important;
}
.custom-table-wrapper-home{
  .table-wrapper{
    width: 87dvw;
  }
  .client{
    display: none;
  }
  .column-header-title{
    font-weight: bold; 
  }
}
  .custom-table-wrapper{
    .table-wrapper{
    background-color: transparent !important;
  }
  table thead {
      display: none;
    }
    table td::before {
      display: none !important;
    }
    
    .modelFix, .status-mobile{
      display: block !important;
    }
    
    table tr {
      display: flex;
      flex-direction: column;
      margin-bottom: 2em;
      border-radius: 20px !important;
      background-color: white;
      td:first-child{
              padding-left: 0.9rem !important;
            }
      td{
        border: none !important;
      }
    }
    table {
      border: 0;
      background-color: transparent !important;
    }
    tbody{
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 25px;
    }
    .wrapper{
      padding-left: 0;
    }
  }
  tr:hover td:first-child::after{
    display: none !important;
  }
}

@media (min-width: 769px) and (max-width: 1367px) and (orientation: landscape) {
  tr:hover td:first-child::after{
          content: "";
          background-color: transparent !important;
        }
}

@media (max-width: 576px) {
  tbody {
          tr {
            td:first-child{
              padding-left: 0.9rem !important;
            }
          }
      }
}

// .custom-table {
//   display: table;
//   width: 100%;
// }


// .custom-table tbody,
// .custom-table tr {
//   display: block;
// }

// .custom-table thead {
//   width: 100%;
// }

// .custom-table tbody {
//   // max-height: 600px;
//   overflow-y: auto;
//   width: 100%;
// }

// .custom-table tr {
//   height: 60px;
//   display: table;
//   table-layout: fixed;
//   width: 100%;
// }
</style>
